import * as api from "@client-shared/services/api";
import * as T from "@shared/types";
import React from "react";
import {
  initialState,
  reducer,
  setAllParamConfig,
  setDefaultRegionLocalisedProductConfig,
  setInitialized,
  setIsMultipleBagEnabled,
  setLocalisedProductConfigByLocale,
  setProductConfig,
  setRegionLargeBagSize,
  setSelectedLocalisedProductConfig,
} from "./actions";
import { AllParamsConfig, ConfigAction, ConfigContextState } from "./types";

import {
  DEFAULT_LONG_TERM_MAX_WEEKLY_INVENTORY,
  DEFAULT_LONG_TERM_MIN_WEEKLY_INVENTORY,
  DEFAULT_SHORT_TERM_MAX_WEEKLY_INVENTORY,
  DEFAULT_SHORT_TERM_MIN_WEEKLY_INVENTORY,
  DEFAULT_USER_LOCALE,
  getDefaultLocaleForCountry,
  getDefaultLocaleForRegion,
  getLocalesForVendorCountry,
  type LocaleSource,
  TERM_OVERRIDE_BAG_SIZE_BY_REGION,
} from "@client-shared/lib/constants";
import type { InventoryConfigNumberValues, InventoryConfigValues, VendorRegionMultipleBagConfig } from "@shared/index";
import { useIsGetAuthRoute } from "../auth/SFDCAuthProvider";
import useVendorContext from "../vendor/useCurrentVendor";
import { getLocalesWithLocalisedProductConfig } from "./configProvider.localizationUtils";

export const defaultConfigState: ConfigContextState = [initialState, () => undefined];

export const ConfigContext = React.createContext(defaultConfigState);

type Props = {
  children: React.ReactNode;
  localeSource?: LocaleSource;
};

export const ConfigProvider: React.FC<Props> = ({ children, localeSource = "region" }: Props) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const isAuthRoute = useIsGetAuthRoute();

  const [{ initialized: isVendorInitialized, currentVendor, regionLocales, currentLocale }] = useVendorContext();
  const currentVendorCountryCode = currentVendor?.countryCode;
  const currentVendorRegion = currentVendor?.region;

  const configLocales = React.useMemo(
    () => getConfigLocalesForVendor({ countryCode: currentVendorCountryCode, region: currentVendorRegion }, regionLocales, localeSource),
    [currentVendorCountryCode, currentVendorRegion, localeSource, regionLocales]
  );

  const isAllParamsConfigAvailable = Boolean(
    state.config.allBagSizesConfig && state.config.allGrindTypesConfig && state.config.inventoryConfig && state.config.multipleBagConfig
  );

  // initialize the config context
  React.useEffect(() => {
    if (isAuthRoute || !isVendorInitialized || !currentVendor) {
      return;
    }
    dispatch(setInitialized(true));
  }, [isAuthRoute, currentVendor, isVendorInitialized]);

  // fetch and set all param config
  React.useEffect(() => {
    if (isAuthRoute || !state.initialized || !currentVendor || !configLocales || isAllParamsConfigAvailable) {
      return;
    }
    void fetchAllParamConfig(currentVendor, dispatch);
  }, [isAuthRoute, currentVendor, configLocales, isAllParamsConfigAvailable, state.initialized]);

  React.useEffect(() => {
    if (isAuthRoute || !state.initialized || !configLocales || !state.config.productConfig || state.config.localisedProductConfigByLocale) {
      return;
    }
    void setLocalisedProductConfigs(configLocales, state.config.productConfig, dispatch);
  }, [isAuthRoute, configLocales, state.config.localisedProductConfigByLocale, state.config.productConfig, state.initialized]);

  React.useEffect(() => {
    if (isAuthRoute || !state.initialized || !currentVendor) {
      return;
    }
    void fetchFormConfig(currentVendor, dispatch);
  }, [isAuthRoute, currentVendor, dispatch, state.initialized]);

  React.useEffect(() => {
    if (!state.initialized || !currentVendor?.region) {
      return;
    }
    dispatch(setRegionLargeBagSize(getRegionLargeBagSize(currentVendor)));
  }, [currentVendor, state.initialized]);

  // utils
  const handleFindTranslatedValue = React.useCallback(
    (key: keyof T.LocalisedProductConfig, value?: string) => {
      if (!value) {
        return;
      }
      return findTranslatedValue(key, value, currentLocale, state.config.localisedProductConfigByLocale);
    },
    [currentLocale, state.config.localisedProductConfigByLocale]
  );

  React.useEffect(() => {
    if (!currentVendor || !state.config.multipleBagConfig) {
      return;
    }
    dispatch(setIsMultipleBagEnabled(checkIsMultipleBagEnabled(state.config.multipleBagConfig, currentVendor)));
  }, [currentVendor, state.config.multipleBagConfig]);

  const handleGetSelectedLocalisedProductConfig = React.useCallback((): T.LocalisedProductConfig | undefined => {
    if (!state.config.localisedProductConfigByLocale || !currentLocale) {
      return undefined;
    }
    return getSelectedLocalisedProductConfig(currentLocale, state.config.localisedProductConfigByLocale);
  }, [state.config.localisedProductConfigByLocale, currentLocale]);

  React.useEffect(() => {
    if (!state.initialized || !state.config.localisedProductConfigByLocale) return;
    const next = getDefaultVendorLocalizedProductConfig(state.config.localisedProductConfigByLocale, {
      countryCode: currentVendorCountryCode,
      region: currentVendorRegion,
    });
    if (!next || next === state.config.defaultRegionLocalisedProductConfig) return;
    dispatch(setDefaultRegionLocalisedProductConfig(next));
  }, [
    state.initialized,
    state.config.localisedProductConfigByLocale,
    state.config.defaultRegionLocalisedProductConfig,
    currentVendorCountryCode,
    currentVendorRegion,
  ]);

  React.useEffect(() => {
    if (!state.initialized) {
      return;
    }
    const selectedLocalisedProductConfig = handleGetSelectedLocalisedProductConfig();
    if (!selectedLocalisedProductConfig) {
      return;
    }
    dispatch(setSelectedLocalisedProductConfig(selectedLocalisedProductConfig));
  }, [state.initialized, handleGetSelectedLocalisedProductConfig]);

  const contextValue: ConfigContextState = React.useMemo(
    () => [
      state,
      dispatch,
      {
        findTranslatedValue: handleFindTranslatedValue,
      },
    ],
    [state, handleFindTranslatedValue]
  );
  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
};

// side effects:

export const fetchAllParamConfig = async (vendor: T.Vendor, dispatch: (action: ConfigAction) => void) => {
  const response = await api.getAllParamConfig(vendor.region, vendor.countryCode);
  const inventoryConfig = convertInventoryConfigToNumbers(response?.inventoryConfig);

  const allParamsConfig: AllParamsConfig = {
    inventoryConfig: inventoryConfig,
    allBagSizesConfig: response.allBagSizesConfig,
    allGrindTypesConfig: response.allGrindTypesConfig,
    multipleBagConfig: response.multipleBagConfig,
    actionsConfig: response.actionsConfig,
    marginConfig: response.marginConfig,
    sltCustomerAccountNumber: response.sltCustomerAccountNumber,
  };
  dispatch(setAllParamConfig(allParamsConfig));
};

export const fetchFormConfig = async (currentVendor: T.Vendor, dispatch: (action: ConfigAction) => void) => {
  if (!currentVendor?.region) {
    return;
  }
  const response = await api.getGlobalProductConfig(currentVendor.region);
  dispatch(setProductConfig(response));
};

// utilities

export const setLocalisedProductConfigs = async (
  locales: T.Locale[],
  productConfig: T.ProductConfig,
  dispatch: (action: ConfigAction) => void
) => {
  const localesWithLocalisedProductConfig = await getLocalesWithLocalisedProductConfig(locales, productConfig);
  dispatch(setLocalisedProductConfigByLocale(localesWithLocalisedProductConfig));
};

export const getConfigLocalesForVendor = (
  vendor: { countryCode?: string | null; region?: string | null } | undefined,
  regionLocales: T.Locale[] | undefined,
  localeSource: LocaleSource = "region"
): T.Locale[] | undefined => {
  if (localeSource === "country") {
    const countryLocales = getLocalesForVendorCountry(vendor?.countryCode)?.map((localeObject) => localeObject.locale);
    if (countryLocales) {
      return countryLocales;
    }
  }
  return regionLocales;
};

export const getRegionLargeBagSize = (currentVendor: T.Vendor | undefined) => {
  if (!currentVendor?.region) {
    return undefined;
  }
  const region = currentVendor.region as keyof typeof TERM_OVERRIDE_BAG_SIZE_BY_REGION;
  return TERM_OVERRIDE_BAG_SIZE_BY_REGION[region];
};

export const checkIsMultipleBagEnabled = (multipleBagConfig: VendorRegionMultipleBagConfig, currentVendor: T.Vendor) => {
  if (multipleBagConfig.enabled) {
    return true;
  }
  if (!currentVendor.vendorKey) {
    return true;
  }
  if (multipleBagConfig.vendorKeys.includes("*")) {
    return true;
  }
  if (multipleBagConfig.vendorKeys.includes(currentVendor.vendorKey)) {
    return true;
  }
  return false;
};

export const getSelectedLocalisedProductConfig = (
  currentLocale: T.Locale,
  localisedProductConfigByLocale?: T.LocaleWithLocalisedProductConfig
): T.LocalisedProductConfig | undefined => {
  return localisedProductConfigByLocale?.[currentLocale];
};

export const convertInventoryConfigToNumbers = (inventoryConfig?: InventoryConfigValues): InventoryConfigNumberValues => {
  if (
    !inventoryConfig?.shortTermMinWeeklyInventory ||
    !inventoryConfig?.shortTermMaxWeeklyInventory ||
    !inventoryConfig?.longTermMinWeeklyInventory ||
    !inventoryConfig?.longTermMaxWeeklyInventory
  ) {
    return {
      shortTermMinWeeklyInventory: DEFAULT_SHORT_TERM_MIN_WEEKLY_INVENTORY,
      shortTermMaxWeeklyInventory: DEFAULT_SHORT_TERM_MAX_WEEKLY_INVENTORY,
      longTermMinWeeklyInventory: DEFAULT_LONG_TERM_MIN_WEEKLY_INVENTORY,
      longTermMaxWeeklyInventory: DEFAULT_LONG_TERM_MAX_WEEKLY_INVENTORY,
    };
  }

  return {
    shortTermMinWeeklyInventory: Number.parseInt(inventoryConfig.shortTermMinWeeklyInventory),
    shortTermMaxWeeklyInventory: Number.parseInt(inventoryConfig.shortTermMaxWeeklyInventory),
    longTermMinWeeklyInventory: Number.parseInt(inventoryConfig.longTermMinWeeklyInventory),
    longTermMaxWeeklyInventory: Number.parseInt(inventoryConfig.longTermMaxWeeklyInventory),
  };
};

export const getDefaultRegionLocalizedProductConfig = (
  localisedProductConfigByLocale: T.LocaleWithLocalisedProductConfig,
  region?: string,
  countryCode?: string | null
) => {
  return getDefaultVendorLocalizedProductConfig(localisedProductConfigByLocale, { region, countryCode });
};

export const getDefaultVendorLocalizedProductConfig = (
  localisedProductConfigByLocale: T.LocaleWithLocalisedProductConfig,
  vendor: { countryCode?: string | null; region?: string | null } | undefined
) => {
  const countryDefaultLocale = getDefaultLocaleForCountry(vendor?.countryCode);
  if (countryDefaultLocale && localisedProductConfigByLocale[countryDefaultLocale]) {
    return localisedProductConfigByLocale[countryDefaultLocale];
  }
  const defaultLocale = (vendor?.region ? getDefaultLocaleForRegion(vendor.region) : undefined) ?? DEFAULT_USER_LOCALE;
  return localisedProductConfigByLocale[defaultLocale];
};

export const findTranslatedValue = (
  key: keyof T.LocalisedProductConfig,
  value: string,
  currentLocale?: T.Locale,
  localeWithLocalisedProductConfig?: T.LocaleWithLocalisedProductConfig
) => {
  if (!currentLocale || !localeWithLocalisedProductConfig?.[currentLocale]) {
    return value;
  }
  if (key === "regionAndCountry") {
    return value;
  }
  const localisedProductConfig = localeWithLocalisedProductConfig[currentLocale];
  if (!localisedProductConfig) {
    return value;
  }
  const options = localisedProductConfig?.[key];
  return options.find((option) => option.value === value)?.label ?? value;
};
